import { useEffect, type ReactNode } from "react";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { ArrowLeft, Home, Package, RotateCw } from "lucide-react";

import { useSession } from "@/lib/session";
import { SiteFooter } from "@/components/SiteCredit";

/**
 * Páginas de sistema: 404 e erro inesperado.
 *
 * Moram na raiz, fora de qualquer layout, então valem para a loja, a área do
 * cliente, o painel e a revenda. O caminho de volta muda com a área de onde
 * a pessoa veio — quem errou um link do painel quer o painel, não a loja.
 */

/** Para onde voltar, pela área do endereço que falhou. */
const useWayBack = (): { to: string; label: string } => {
  const { pathname } = useLocation();
  const { profile } = useSession();

  if (pathname.startsWith("/admin")) return { to: "/admin/pedidos", label: "Voltar ao painel" };
  if (pathname.startsWith("/revendedor")) return { to: "/revendedor", label: "Voltar à revenda" };
  if (pathname.startsWith("/minha-conta") || profile?.role === "CLIENTE")
    return { to: "/minha-conta/pedidos", label: "Ver meus pedidos" };
  return { to: "/", label: "Montar meu uniforme" };
};

/** A camiseta de costas, com o código no lugar do número. */
function Jersey({ number, name }: { number: string; name: string }) {
  return (
    <div className="preview-stage relative mx-auto aspect-square w-full max-w-[22rem] overflow-hidden rounded-mb-card">
      <img
        src="/assets/camiseta-costas.png"
        alt=""
        draggable={false}
        className="absolute inset-0 h-full w-full select-none object-contain"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-[24%] flex flex-col items-center font-display font-bold text-mb-navy"
      >
        <span className="text-[clamp(0.8rem,3.4vw,1.15rem)] tracking-[0.25em]">{name}</span>
        <span className="mt-1 text-[clamp(4rem,19vw,6.5rem)] leading-none tabular-nums">
          {number}
        </span>
      </div>
    </div>
  );
}

function Frame({
  tabTitle,
  jersey,
  eyebrow,
  title,
  children,
  actions,
}: {
  tabTitle: string;
  jersey: ReactNode;
  eyebrow: string;
  title: string;
  children: ReactNode;
  actions: ReactNode;
}) {
  // A rota que falhou não declara head(): sem isto, a aba ficaria com o
  // título da loja, como se a página tivesse aberto.
  useEffect(() => {
    document.title = `${tabTitle} — MonteBox`;
  }, [tabTitle]);

  return (
    <div className="flex min-h-screen flex-col bg-mb-surface">
      <header className="px-4 py-5 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2.5" aria-label="MonteBox — início">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-mb-yellow shadow-cta">
            <Package className="h-5 w-5 text-mb-ink" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-mb-navy">
            MonteBox
          </span>
        </Link>
      </header>

      <main className="mx-auto grid w-full max-w-5xl grow items-center gap-8 px-4 pb-16 sm:px-8 md:grid-cols-2 md:gap-12">
        <div className="order-2 md:order-1">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-mb-muted">{eyebrow}</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-mb-navy text-balance sm:text-5xl">
            {title}
          </h1>
          <div className="mt-4 max-w-md text-base text-mb-muted text-pretty">{children}</div>
          <div className="mt-8 flex flex-wrap gap-3">{actions}</div>
        </div>
        <div className="order-1 md:order-2">{jersey}</div>
      </main>
      <SiteFooter />
    </div>
  );
}

const primary =
  "inline-flex items-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5";
const secondary =
  "inline-flex items-center gap-2 rounded-mb-button border border-mb-line bg-white px-5 py-3.5 text-sm font-semibold text-mb-navy transition-colors hover:border-mb-navy";

export function NotFoundPage() {
  const way = useWayBack();
  const { pathname } = useLocation();

  return (
    <Frame
      tabTitle="Página não encontrada"
      jersey={<Jersey number="404" name="FORA DO JOGO" />}
      eyebrow="Erro 404"
      title="Essa página saiu de campo."
      actions={
        <>
          <Link to={way.to} className={primary}>
            <Home className="h-[18px] w-[18px]" />
            {way.label}
          </Link>
          <button type="button" onClick={() => window.history.back()} className={secondary}>
            <ArrowLeft className="h-[18px] w-[18px]" />
            Voltar
          </button>
        </>
      }
    >
      <p>
        Não encontramos{" "}
        <code className="rounded bg-white px-1.5 py-0.5 font-mono text-[0.85em] text-mb-ink">
          {pathname}
        </code>
        . O endereço pode ter mudado ou ter sido digitado com algum erro.
      </p>
    </Frame>
  );
}

export function ErrorPage({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  const way = useWayBack();
  // O detalhe técnico fica no console para quem for investigar; na tela, só o
  // que a pessoa pode fazer.
  console.error(error);

  return (
    <Frame
      tabTitle="Algo deu errado"
      jersey={<Jersey number="500" name="INTERVALO" />}
      eyebrow="Algo deu errado"
      title="Essa página não carregou."
      actions={
        <>
          <button
            type="button"
            onClick={() => {
              void router.invalidate();
              reset();
            }}
            className={primary}
          >
            <RotateCw className="h-[18px] w-[18px]" />
            Tentar de novo
          </button>
          <a href={way.to} className={secondary}>
            <Home className="h-[18px] w-[18px]" />
            {way.label}
          </a>
        </>
      }
    >
      <p>
        Foi um problema do nosso lado, não seu. Tente de novo em instantes — o que você já salvou
        continua guardado.
      </p>
    </Frame>
  );
}
